import { useContext, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { AppContext } from '../../AppContext.tsx';
import messages from './Totals.messages.ts';
import './Totals.scss';
import { NumberToPercentage, USDollar } from '@luna-protocol/core/src/utils/constants/format.ts';
import { useGetSerialNumbers } from '../../queries/useGetSerialNumbers.ts';

interface Totals {
  baseProductTotal?: number;
  totalApprovedAmount?: number;
}

const Totals = (totalProp: Totals) => {
  const { baseProductTotal = 0, totalApprovedAmount = 0 } = totalProp;
  const { formatMessage } = useIntl();
  const { totals, setTotals, deal, loanApplicationID } = useContext(AppContext);
  const { data: serialNumberData } = useGetSerialNumbers(loanApplicationID);

  const financiables = deal && deal.accessories?.filter(acc => acc.is_financeable);
  const financiableTotals = financiables?.reduce((acc, curr) => acc + Number(curr.price), 0) || 0;
  const sellerPurchasePrice =
    serialNumberData?.serial_numbers?.reduce((acc, curr) => acc + Number(curr.seller_purchase_price), 0) || 0;

  const basePrice = (totals?.base_accessories && financiableTotals + baseProductTotal) || baseProductTotal;
  // this should include new accessory items
  const backendCollateralValue = totals?.backend_products || 0;

  // total request
  const totalRequested = () => {
    if (totals) {
      return (
        (baseProductTotal || 0) +
        (financiableTotals || 0) +
        (totals.additions || 0) /* tax and fee */ +
        (totals.backend_products || 0) -
        (totals.deductions || 0)
      );
    }
    return 0;
  };

  const getFrontendTotalValue = () => {
    if (totals) {
      return (
        (baseProductTotal || 0) +
        (financiableTotals || 0) +
        (totals.additions || 0) /* tax and fee */ -
        (totals.deductions || 0)
      );
    }
    return 0;
  };

  const frontendLtvPercentage = useMemo(() => {
    return (getFrontendTotalValue() / sellerPurchasePrice || 0) * 100;
  }, [getFrontendTotalValue, sellerPurchasePrice]);

  const backendLtvPercentage = useMemo(() => {
    if (totals && totals.backend_products) {
      return (totals.backend_products / sellerPurchasePrice || 0) * 100;
    }
    return 0;
  }, [totals, backendCollateralValue]);

  const frontendLTVLimit = 120;
  const backendLTVLimit = 15;

  useEffect(() => {
    const total = totalRequested();
    if (total !== totals?.total_requested) {
      setTotals({
        ...totals,
        total_requested: total,
        isFrontendLTVExceed: frontendLTVLimit < frontendLtvPercentage,
        isBackendLTVExceed: backendLTVLimit < backendLtvPercentage,
      });
    }
  }, [totals]);

  return (
    <div className="totals-container">
      <div className="totals-container--title">
        <h2>{formatMessage(messages.title)}</h2>
        <h2>{USDollar.format(totalApprovedAmount)}</h2>
      </div>
      <div className="totals-container--list">
        <div className="totals-container--column">
          <div className="totals-container--column--content">
            <div className="totals-container--column-title">{formatMessage(messages.basePrice)}</div>
            <div className="totals-container--column-value">{USDollar.format(basePrice)}</div>
          </div>
        </div>
        <div className="totals-container--column">
          <div className="totals-container--column--content">
            <div className="totals-container--column-title">{formatMessage(messages.feesTitle)}</div>
            <div className="totals-container--column-value">{USDollar.format(totals?.additions || 0)}</div>
          </div>
        </div>
        <div className="totals-container--column">
          <div className="totals-container--column--content">
            <div className="totals-container--column-title">{formatMessage(messages.deductionsTitle)}</div>
            <div className="totals-container--column-value">{USDollar.format(totals?.deductions || 0)}</div>
          </div>
        </div>
        <div className="totals-container--column">
          <div className="totals-container--column--content">
            <div className="totals-container--column-title">{formatMessage(messages.totalRequested)}</div>
            <div className="totals-container--column-value">{USDollar.format(totalRequested() || 0)}</div>
          </div>
        </div>
      </div>
      <div className="totals-container--list dropped">
        <div className="totals-container--column dropped">
          <div className="totals-container--column--content">
            <div className="totals-container--column-title">{formatMessage(messages.frontendLTV)}</div>
            <div className={`totals-container--column-value ${totals?.isFrontendLTVExceed ? `negative` : ''}`}>
              {`${USDollar.format(getFrontendTotalValue() || 0)} - ${NumberToPercentage(frontendLtvPercentage)}`}
            </div>
          </div>
        </div>
        <div className="totals-container--column dropped">
          <div className="totals-container--column--content">
            <div className="totals-container--column-title">{formatMessage(messages.productsTitle)}</div>
            <div className="totals-container--column-value">{USDollar.format(totals?.backend_products || 0)}</div>
          </div>
        </div>
        <div className="totals-container--column dropped">
          <div className="totals-container--column--content">
            <div className="totals-container--column-title">{formatMessage(messages.backendLTV)}</div>
            <div className={`totals-container--column-value ${totals?.isBackendLTVExceed ? `negative` : ''}`}>
              {`${USDollar.format(totals?.backend_products || 0)} - ${NumberToPercentage(backendLtvPercentage || 0)}`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Totals;
